/**
 * Memoria de la sesion en MODO PAGINA.
 *
 * <p>Un checkout alojado tiene que sobrevivir a la ida y vuelta a la pasarela. El comprador sale de
 * aqui hacia Transbank o MercadoPago y vuelve a la `return_url` con **solo el token de la pasarela**:
 * ni `client_secret` ni nada nuestro. Sin recordar la sesion, la pagina de retorno no puede
 * identificar el intent, no dispara el segundo confirm, y la pasarela termina abortando una
 * transaccion que el comprador ya pago.
 *
 * <p>Embebido esto NO hace falta —el SDK vuelve a inyectar el `client_secret` en el iframe— y por eso
 * solo se usa cuando el checkout es el documento superior.
 *
 * <p><strong>Por que sessionStorage y no otra cosa:</strong> vive en el origen del checkout (no en el
 * del comercio), muere al cerrar la pestana, y no viaja en ninguna peticion como haria una cookie.
 * El `client_secret` ya estaba en la URL de esta misma pagina, asi que no se amplia a quien puede
 * verlo; y por si solo no basta para cobrar: la API exige ademas la clave publicable del tenant.
 */
import type { SessionFragment } from './parseFragment';
import { intentIdFromClientSecret } from './parseFragment';

const STORAGE_KEY = 'apipay.checkout.session';

/**
 * Clave APARTE de la sesion, y a proposito: la inscripcion nace y muere dentro de una sesion que ya
 * esta recordada, y mezclarlas obligaria a cada escritura de una a preservar los campos de la otra.
 */
const INSCRIPTION_KEY = 'apipay.checkout.inscription';

/**
 * Tercera clave, y por la misma razon que la segunda: una salida hacia la pasarela puede existir sin
 * inscripcion ninguna, y mezclarlas obligaria a cada escritura a preservar los campos de la otra.
 */
const REDIRECT_KEY = 'apipay.checkout.redirect';

/**
 * Cuarta clave: la vuelta a la tienda, para la pantalla en la que el intent NO esta.
 *
 * <p>`merchant_return_url` vive dentro del intent, asi que cuando el GET del intent falla el widget
 * no tiene ninguna salida que ofrecer y la pantalla de error se convierte en un callejon sin salida:
 * en modo pagina el checkout ES la pestana, y al comprador solo le queda cerrarla a mano --si venia
 * de pagar en la pasarela, cerrarla justo despues de pagar--. Recordandola en la carga que SI
 * funciono, la que falla despues sigue teniendo por donde salir.
 *
 * <p>Va aparte por lo mismo que las otras dos, y ademas porque se escribe en otro momento: la sesion
 * se recuerda en el arranque y esto solo se sabe cuando el intent ya respondio.
 */
const MERCHANT_RETURN_KEY = 'apipay.checkout.merchant-return';

/**
 * Quinta clave: la vuelta de la pasarela reconocida por la URL, no por la salida recordada.
 *
 * <p>La tercera clave (`REDIRECT_KEY`) solo sabe de una vuelta si la IDA paso por esta misma
 * pestana. En movil no pasa: el comprador sale hacia la app del banco y vuelve en otra pestana, y
 * alli no hay ninguna salida apuntada. Medido en QA el 2026-09-16 con `pi_shfZPUkbg9GzZLYMkHAxA1Nb`:
 * al volver de pagar, el checkout le ofrecia pagar OTRA VEZ.
 *
 * <p>Lo que cruza de una pestana a otra es la URL, asi que la plataforma marca alli el retorno y el
 * arranque lo copia AQUI. Se copia y no se usa al vuelo por dos razones: la marca se borra de la
 * barra de direcciones en cuanto se lee --sigue al lado del `client_secret`-- y esta pantalla se
 * remonta sola varias veces por cobro; sin copiar, el segundo montaje volveria al punto de partida.
 *
 * <p>Va aparte de las otras cuatro por lo mismo que ellas: se escribe en otro momento --en el
 * arranque, y solo cuando la URL lo dice-- y mezclarla obligaria a cada escritura de las demas a
 * preservar sus campos.
 */
const GATEWAY_RETURN_KEY = 'apipay.checkout.gateway-return';

/**
 * Sexta clave: la sesion de pasarela YA SE PIDIO para este intent.
 *
 * <p>No es una salida ni una vuelta —aqui nadie navega a ninguna parte— sino la otra mitad de la
 * misma regla: **a un intent al que ya se le pidio algo una vez no se le vuelve a pedir solo**. La
 * necesitan las estrategias que se montan EMBEBIDAS y cuyo formulario no existe hasta que un primer
 * confirm crea la sesion en la pasarela (BCI sobre Mastercard Gateway es la unica hoy).
 *
 * <p><strong>Por que no basta un `useRef`</strong>: un ref de «ya arranque» nace de cero en cada
 * montaje. Si el confirm no llega a publicar sesion, la pantalla se remonta en el mismo estado, el
 * ref esta otra vez en `false` y el arranque automatico vuelve a disparar. Eso es un bucle de
 * confirms contra el backend, que es lo mismo que {@link PendingRedirect#returnedAt} evita del lado
 * de las redirecciones: el ref cubre el DOBLE disparo dentro de un montaje, y esta marca cubre los
 * montajes siguientes.
 *
 * <p>Va aparte de las otras cinco por lo mismo que ellas: se escribe en otro momento y mezclarla
 * obligaria a cada escritura de las demas a preservar sus campos.
 */
const GATEWAY_SESSION_KEY = 'apipay.checkout.gateway-session';

interface StoredSession {
  clientSecret: string;
  /**
   * La clave PUBLICABLE, y no es un extra: sin ella el retorno tampoco funciona.
   *
   * <p>Se recordaba solo el `client_secret`, pero App exige las DOS cosas para reconstruir la sesion
   * y la publicable se leia unicamente del parametro `pk` de la URL. Resultado: el comprador pagaba
   * en Transbank, volvia a la `return_url` con solo el token de la pasarela, el widget no podia
   * identificar la sesion y el segundo confirm NUNCA se disparaba. El pago quedaba PENDING y el
   * dinero en el limbo. Este archivo ya decia en su cabecera que del retorno se vuelve "con solo el
   * token de la pasarela": recordaba la mitad de lo que hacia falta para cumplirlo.
   *
   * <p>Guardarla no amplia a quien puede verla: es PUBLICABLE, viajaba en la URL de esta misma
   * pagina, y sessionStorage vive en el origen del checkout y muere al cerrar la pestana.
   */
  publicKey: string | null;
}

/** Lo que el checkout necesita para reconstruir una sesion tras volver de la pasarela. */
export interface RecalledSession {
  readonly fragment: SessionFragment;
  readonly publicKey: string | null;
}

function asNonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function read(): StoredSession | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null) {
      const record = parsed as { clientSecret?: unknown; publicKey?: unknown };
      const clientSecret = asNonEmptyString(record.clientSecret);
      if (clientSecret !== null) {
        return { clientSecret, publicKey: asNonEmptyString(record.publicKey) };
      }
    }
    return null;
  } catch {
    // Modo incognito, almacenamiento bloqueado o JSON corrupto: se sigue sin memoria.
    return null;
  }
}

function write(clientSecret: string, publicKey: string | null): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ clientSecret, publicKey }));
  } catch {
    // Sin almacenamiento la ida funciona igual; solo se pierde el retorno.
  }
}

/**
 * Borra la sesion recordada, con la inscripcion en curso incluida. Se llama al llegar a un estado
 * final: una inscripcion abierta para un intent ya resuelto no tiene a donde ir.
 */
export function forgetHostedSession(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nada que hacer: si no se puede escribir, tampoco se pudo guardar.
  }
  forgetPendingInscription();
  forgetPendingRedirect();
  forgetMerchantReturnUrl();
  forgetGatewayReturn();
  forgetGatewaySessionRequest();
}

/**
 * Peticion de sesion de pasarela ya hecha para un intent (ver {@link GATEWAY_SESSION_KEY}).
 *
 * <p>Lleva el `gatewayId` porque el comprador puede cambiar de medio de pago sin cambiar de intent:
 * haber pedido la sesion de BCI no dice nada sobre la que necesitaria otra pasarela.
 */
export interface GatewaySessionRequest {
  readonly intentId: string;
  readonly gatewayId: string;
}

function asGatewaySessionRequest(value: unknown): GatewaySessionRequest | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const record = value as { intentId?: unknown; gatewayId?: unknown };
  const intentId = asNonEmptyString(record.intentId);
  const gatewayId = asNonEmptyString(record.gatewayId);
  return intentId === null || gatewayId === null ? null : { intentId, gatewayId };
}

/** Apunta que la sesion de la pasarela YA se pidio. Se llama justo antes de pedirla, no despues. */
export function rememberGatewaySessionRequest(intentId: string, gatewayId: string): void {
  if (intentId.length === 0 || gatewayId.length === 0) {
    return;
  }
  try {
    window.sessionStorage.setItem(
      GATEWAY_SESSION_KEY,
      JSON.stringify({ intentId, gatewayId } satisfies GatewaySessionRequest),
    );
  } catch {
    // Sin almacenamiento el arranque funciona igual; lo que se pierde es sujetarlo a traves de los
    // remontajes. El ref en memoria de la estrategia sigue cubriendo el doble disparo del montaje.
  }
}

/**
 * Peticion ya hecha para ESTE intent y ESTA pasarela, o null.
 *
 * @param intentId intent que se esta pagando; una marca de otro intent se ignora, porque en una
 *     misma pestana el comprador puede pasar por dos checkouts
 * @param gatewayId pasarela activa; la marca de otra pasarela tampoco cuenta
 */
export function recallGatewaySessionRequest(
  intentId: string | null,
  gatewayId: string,
): GatewaySessionRequest | null {
  if (intentId === null || intentId.length === 0 || gatewayId.length === 0) {
    return null;
  }
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(GATEWAY_SESSION_KEY);
  } catch {
    return null;
  }
  if (raw === null) {
    return null;
  }
  let pedida: GatewaySessionRequest | null;
  try {
    pedida = asGatewaySessionRequest(JSON.parse(raw));
  } catch {
    // JSON corrupto o manipulado: se sigue sin marca, como si nunca se hubiera pedido.
    return null;
  }
  return pedida !== null && pedida.intentId === intentId && pedida.gatewayId === gatewayId
    ? pedida
    : null;
}

/** Olvida la peticion apuntada. La llama {@link forgetHostedSession} al cerrar la sesion. */
export function forgetGatewaySessionRequest(): void {
  try {
    window.sessionStorage.removeItem(GATEWAY_SESSION_KEY);
  } catch {
    // Nada que hacer: si no se puede escribir, tampoco se pudo guardar.
  }
}

/**
 * Inscripcion de tarjeta abierta y todavia sin cerrar.
 *
 * <p>Se recuerda el `intent_id` junto al `ins_...` para no cerrar contra un intent la inscripcion de
 * otro: en una misma pestana el comprador puede pasar por dos checkouts distintos, y `sessionStorage`
 * no distingue uno de otro por si solo.
 *
 * <p>El `gateway_id` viaja tambien porque al VOLVER de la pasarela el widget arranca de cero: la
 * eleccion que el comprador hizo en el selector ya no esta en memoria, y sin ella el despachador no
 * sabria que estrategia montar para cerrar lo que quedo a medias.
 */
export interface PendingInscription {
  readonly intentId: string;
  readonly inscriptionId: string;
  readonly gatewayId: string;
}

function asPendingInscription(value: unknown): PendingInscription | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const record = value as { intentId?: unknown; inscriptionId?: unknown; gatewayId?: unknown };
  const intentId = asNonEmptyString(record.intentId);
  const inscriptionId = asNonEmptyString(record.inscriptionId);
  const gatewayId = asNonEmptyString(record.gatewayId);
  if (intentId === null || inscriptionId === null || gatewayId === null) {
    return null;
  }
  return { intentId, inscriptionId, gatewayId };
}

/**
 * Recuerda la inscripcion ANTES de mandar al comprador al formulario de la pasarela.
 *
 * <p>Sin esto el retorno es irrecuperable: la pasarela devuelve al comprador a la `return_url` sin
 * nada nuestro, y el `ins_...` es lo unico con lo que el servidor puede cerrar la inscripcion y
 * asociar la tarjeta al intent. Es el mismo agujero que costaba pagos cuando solo se recordaba la
 * mitad de la sesion (ver {@link StoredSession}).
 *
 * <p>No hay dato de tarjeta que guardar: el `ins_...` es un identificador de recurso nuestro.
 */
export function rememberPendingInscription(pending: PendingInscription): void {
  try {
    window.sessionStorage.setItem(INSCRIPTION_KEY, JSON.stringify(pending));
  } catch {
    // Sin almacenamiento la ida al formulario funciona igual; lo que se pierde es el cierre.
  }
}

/**
 * Inscripcion pendiente de ESTE intent, o null.
 *
 * @param intentId intent que se esta pagando; un registro de otro intent se ignora en vez de
 *     cerrarse contra el que no le corresponde
 */
export function recallPendingInscription(intentId: string | null): PendingInscription | null {
  if (intentId === null || intentId.length === 0) {
    return null;
  }
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(INSCRIPTION_KEY);
  } catch {
    return null;
  }
  if (raw === null) {
    return null;
  }
  let pending: PendingInscription | null;
  try {
    pending = asPendingInscription(JSON.parse(raw));
  } catch {
    // JSON corrupto o manipulado: se sigue sin memoria, como si nunca se hubiera inscrito nada.
    return null;
  }
  return pending !== null && pending.intentId === intentId ? pending : null;
}

/** Olvida la inscripcion pendiente. Se llama justo antes de cerrarla, para no intentarlo dos veces. */
export function forgetPendingInscription(): void {
  try {
    window.sessionStorage.removeItem(INSCRIPTION_KEY);
  } catch {
    // Nada que hacer: si no se puede escribir, tampoco se pudo guardar.
  }
}

/**
 * Salida hacia una pasarela de REDIRECCION cuyo desenlace no vuelve con el comprador.
 *
 * <p>Con Webpay el retorno se reconoce solo: trae `token_ws` en la URL y ese token es ademas lo que
 * hace falta para cerrar el cobro. Con Getnet y con CompraAqui no hay nada de eso. La verdad la trae
 * el webhook, y del retorno no se puede depender:
 *
 * <ul>
 *   <li>Getnet <strong>no documenta</strong> ni el metodo HTTP del retorno ni los parametros que
 *       anade a la URL (laguna L-3 de su contrato), asi que no hay ningun nombre de parametro que
 *       este widget pueda buscar sin inventarselo;
 *   <li>CompraAqui vuelve con un `jwt=`, pero notifica al backend ANTES de redirigir, de modo que
 *       cuando el comprador aterriza aqui el pago ya esta decidido.
 * </ul>
 *
 * <p>Sin recordar la salida, el retorno con el intent todavia en `REQUIRES_ACTION` —el webhook puede
 * tardar— le vuelve a pintar al comprador el boton de pagar sobre un pago que quiza ya hizo. Con
 * ella, el widget sabe que toca esperar el desenlace y no volver a empezar.
 *
 * <p>Se guarda el `intent_id` por el mismo motivo que en la inscripcion: en una misma pestana el
 * comprador puede pasar por dos checkouts, y `sessionStorage` no los distingue por si solo.
 */
export interface PendingRedirect {
  readonly intentId: string;
  readonly gatewayId: string;
  /**
   * Marca de que el comprador YA VOLVIO de este viaje.
   *
   * <p>Existe por un bucle real, visto en Getnet el 2026-08-20: el comprador cancelaba en la
   * pasarela, volvia al checkout y el checkout lo mandaba de vuelta a la misma pantalla de rechazo,
   * una y otra vez.
   *
   * <p>La causa era que al reconocer el retorno se BORRABA el registro. Con la pasarela cancelada el
   * intent se queda en `REQUIRES_ACTION` --Getnet reporta la sesion como PENDING, que es la verdad:
   * nadie pago-- asi que el widget vuelve a montar la estrategia; sin registro, esta cree que el
   * comprador nunca salio y el auto-arranque del modo pagina lo reenvia. Y otra vez.
   *
   * <p>Por eso el registro ya no se borra al volver: se MARCA. Un viaje marcado significa «este
   * comprador ya fue y ya volvio», y con eso el auto-arranque se queda quieto aunque la estrategia se
   * monte de cero. El comprador conserva el boton para reintentar a mano, que es un gesto suyo y no
   * un rebote automatico.
   */
  readonly returnedAt?: number | undefined;
}

function asPendingRedirect(value: unknown): PendingRedirect | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const record = value as { intentId?: unknown; gatewayId?: unknown; returnedAt?: unknown };
  const intentId = asNonEmptyString(record.intentId);
  const gatewayId = asNonEmptyString(record.gatewayId);
  if (intentId === null || gatewayId === null) {
    return null;
  }
  const returnedAt =
    typeof record.returnedAt === 'number' && Number.isFinite(record.returnedAt)
      ? record.returnedAt
      : undefined;
  return returnedAt === undefined ? { intentId, gatewayId } : { intentId, gatewayId, returnedAt };
}

/** Recuerda la salida ANTES de navegar: despues de navegar ya no se ejecuta nada de esta pagina. */
export function rememberPendingRedirect(pending: PendingRedirect): void {
  try {
    window.sessionStorage.setItem(REDIRECT_KEY, JSON.stringify(pending));
  } catch {
    // Sin almacenamiento la ida funciona igual; lo que se pierde es reconocer el retorno.
  }
}

/**
 * Salida pendiente de ESTE intent, o null.
 *
 * @param intentId intent que se esta pagando; un registro de otro intent se ignora
 */
export function recallPendingRedirect(intentId: string | null): PendingRedirect | null {
  if (intentId === null || intentId.length === 0) {
    return null;
  }
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(REDIRECT_KEY);
  } catch {
    return null;
  }
  if (raw === null) {
    return null;
  }
  let pending: PendingRedirect | null;
  try {
    pending = asPendingRedirect(JSON.parse(raw));
  } catch {
    // JSON corrupto o manipulado: se sigue sin memoria, como si nunca se hubiera salido.
    return null;
  }
  return pending !== null && pending.intentId === intentId ? pending : null;
}

/**
 * Marca el viaje como YA VUELTO, sin borrarlo.
 *
 * <p>Se llama al reconocer el retorno. Antes aqui se borraba el registro, y eso es lo que producia el
 * bucle descrito en {@link PendingRedirect#returnedAt}: sin registro, un remontaje de la estrategia
 * cree que el comprador nunca salio y el auto-arranque lo devuelve a la pasarela.
 *
 * <p>Devuelve el registro marcado, o null si no habia ninguno para ese intent.
 */
export function markPendingRedirectReturned(intentId: string | null): PendingRedirect | null {
  const pending = recallPendingRedirect(intentId);
  if (pending === null) {
    return null;
  }
  if (pending.returnedAt !== undefined) {
    return pending;
  }
  const marked: PendingRedirect = { ...pending, returnedAt: Date.now() };
  try {
    window.sessionStorage.setItem(REDIRECT_KEY, JSON.stringify(marked));
  } catch {
    // Sin almacenamiento no hay marca que poner; el auto-arranque se protege ademas con su propio
    // ref en memoria, que cubre el caso de que la estrategia no llegue a desmontarse.
  }
  return marked;
}

/**
 * Olvida la salida pendiente.
 *
 * <p>Se llama al DESHACER un viaje que no llego a ocurrir --la navegacion fue bloqueada-- y al
 * cerrar la sesion en un estado terminal. Ya NO se llama al volver de la pasarela: para eso esta
 * {@link markPendingRedirectReturned}, y la diferencia entre las dos es justo lo que evita el bucle.
 */
export function forgetPendingRedirect(): void {
  try {
    window.sessionStorage.removeItem(REDIRECT_KEY);
  } catch {
    // Nada que hacer: si no se puede escribir, tampoco se pudo guardar.
  }
}

/**
 * Vuelta de la pasarela reconocida por la URL (ver {@link GATEWAY_RETURN_KEY}).
 *
 * <p>No lleva `gatewayId`: la marca de la URL no lo dice, y quien la lee ya tiene el intent delante
 * con su pasarela dentro. Es a proposito que no lo invente.
 */
export interface GatewayReturn {
  readonly intentId: string;
  /**
   * Marca de que un montaje anterior YA la atendio, y es la gemela de
   * {@link PendingRedirect#returnedAt}: existe por el mismo motivo y evita el mismo bucle.
   *
   * <p>Si la vuelta se BORRARA al atenderla, un remontaje --que aqui ocurre siempre que el cobro no
   * se resuelve a la primera-- volveria a verla como una visita nueva. Y si no se marcara de ninguna
   * forma, cada remontaje dispararia otra consulta: un bucle que se ve menos y molesta igual.
   */
  readonly acknowledgedAt?: number | undefined;
  /**
   * Indicador de pago aprobado que traia la URL de vuelta, cuando la pasarela lo pone.
   *
   * <p>Hoy solo lo pone BCI (`resultIndicator`). Se copia aqui por lo mismo que la marca: la URL se
   * limpia en cuanto se lee y esta pantalla se remonta varias veces por cobro, asi que sin copiarlo
   * el segundo montaje volveria al punto de partida.
   *
   * <p><strong>No es una credencial y no decide nada.</strong> Lo unico que hace es viajar al
   * backend en el confirm para que lo cruce con la copia que guardo al crear la sesion; el veredicto
   * lo firma RETRIEVE ORDER. Guardarlo no amplia a quien puede verlo: venia en la barra de
   * direcciones de esta misma pagina, y `sessionStorage` vive en el origen del checkout y muere al
   * cerrar la pestana.
   */
  readonly resultIndicator?: string | undefined;
}

function asGatewayReturn(value: unknown): GatewayReturn | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const record = value as {
    intentId?: unknown;
    acknowledgedAt?: unknown;
    resultIndicator?: unknown;
  };
  const intentId = asNonEmptyString(record.intentId);
  if (intentId === null) {
    return null;
  }
  const acknowledgedAt =
    typeof record.acknowledgedAt === 'number' && Number.isFinite(record.acknowledgedAt)
      ? record.acknowledgedAt
      : undefined;
  const resultIndicator = asNonEmptyString(record.resultIndicator) ?? undefined;
  const marca: GatewayReturn = { intentId };
  return {
    ...marca,
    ...(acknowledgedAt === undefined ? {} : { acknowledgedAt }),
    ...(resultIndicator === undefined ? {} : { resultIndicator }),
  };
}

/**
 * Copia a esta pestana la marca de retorno que traia la URL. La llama el arranque, una sola vez.
 *
 * @param intentId intent que se estaba pagando
 * @param resultIndicator indicador de pago aprobado, si la pasarela lo puso en la URL de vuelta
 */
export function rememberGatewayReturn(
  intentId: string | null,
  resultIndicator: string | null = null,
): void {
  if (intentId === null || intentId.length === 0) {
    return;
  }
  const marca: GatewayReturn =
    resultIndicator === null || resultIndicator.length === 0
      ? { intentId }
      : { intentId, resultIndicator };
  try {
    window.sessionStorage.setItem(GATEWAY_RETURN_KEY, JSON.stringify(marca));
  } catch {
    // Sin almacenamiento la vuelta se reconoce igual en el PRIMER montaje --la marca ya se leyo de
    // la URL-- y lo que se pierde es sujetarla a traves de los remontajes.
  }
}

/**
 * Vuelta marcada para ESTE intent, o null.
 *
 * @param intentId intent que se esta pagando; una marca de otro intent se ignora, porque en una
 *     misma pestana el comprador puede pasar por dos checkouts
 */
export function recallGatewayReturn(intentId: string | null): GatewayReturn | null {
  if (intentId === null || intentId.length === 0) {
    return null;
  }
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(GATEWAY_RETURN_KEY);
  } catch {
    return null;
  }
  if (raw === null) {
    return null;
  }
  let marca: GatewayReturn | null;
  try {
    marca = asGatewayReturn(JSON.parse(raw));
  } catch {
    // JSON corrupto o manipulado: se sigue sin marca, como si no hubiera vuelto de ninguna parte.
    return null;
  }
  return marca !== null && marca.intentId === intentId ? marca : null;
}

/**
 * Marca la vuelta como ATENDIDA, sin borrarla. Gemela de {@link markPendingRedirectReturned}, y por
 * el mismo motivo: borrar es lo que produce el bucle.
 *
 * <p>Devuelve la marca ya atendida, o null si no habia ninguna para ese intent.
 */
export function markGatewayReturnAcknowledged(intentId: string | null): GatewayReturn | null {
  const marca = recallGatewayReturn(intentId);
  if (marca === null || marca.acknowledgedAt !== undefined) {
    return marca;
  }
  const atendida: GatewayReturn = { ...marca, acknowledgedAt: Date.now() };
  try {
    window.sessionStorage.setItem(GATEWAY_RETURN_KEY, JSON.stringify(atendida));
  } catch {
    // Sin almacenamiento no hay marca que poner; el montaje se protege ademas con su propio ref.
  }
  return atendida;
}

/** Olvida la marca de retorno. La llama {@link forgetHostedSession} al cerrar la sesion. */
export function forgetGatewayReturn(): void {
  try {
    window.sessionStorage.removeItem(GATEWAY_RETURN_KEY);
  } catch {
    // Nada que hacer: si no se puede escribir, tampoco se pudo guardar.
  }
}

/**
 * Guarda la sesion cuando viene en la URL y la recupera cuando no.
 *
 * <p>Y COPIA LA MARCA DE RETORNO que venia con ella, que es lo unico que sobrevive a volver en otra
 * pestana (ver {@link GATEWAY_RETURN_KEY}). Se hace aqui, y no en el bootstrap, porque este es el
 * punto donde ya se sabe con que `client_secret` se queda la carga --y el intent va dentro de el--,
 * y porque asi la copia ocurre exactamente una vez por carga, igual que la lectura de la URL.
 *
 * @param fragment lo que venia en el fragment de esta carga
 * @param publicKey la clave publicable del parametro `pk`, o null si no venia
 * @returns el fragment y la clave publicable, recuperados del almacen si hacia falta
 */
export function rememberOrRecallSession(
  fragment: SessionFragment,
  publicKey: string | null,
): RecalledSession {
  const recalled = resolveSession(fragment, publicKey);
  if (fragment.gatewayReturn && recalled.fragment.clientSecret !== null) {
    rememberGatewayReturn(
      intentIdFromClientSecret(recalled.fragment.clientSecret),
      fragment.gatewayResultIndicator,
    );
  }
  return recalled;
}

function resolveSession(fragment: SessionFragment, publicKey: string | null): RecalledSession {
  if (fragment.clientSecret !== null) {
    write(fragment.clientSecret, publicKey);
    return { fragment, publicKey };
  }
  const stored = read();
  if (stored === null) {
    return { fragment, publicKey };
  }
  return {
    fragment: {
      clientSecret: stored.clientSecret,
      nonce: fragment.nonce,
      gatewayReturn: fragment.gatewayReturn,
      gatewayResultIndicator: fragment.gatewayResultIndicator,
    },
    // La URL manda si trae `pk`: es la de ESTA carga. El almacen solo cubre el hueco, que es
    // justo el caso del retorno de la pasarela.
    publicKey: publicKey ?? stored.publicKey,
  };
}

/**
 * Recuerda a donde vuelve el comprador, para poder ofrecerselo AUNQUE el intent no cargue.
 *
 * <p>Se guarda el `intent_id` al lado por lo mismo que en la inscripcion y en la salida pendiente:
 * en una misma pestana el comprador puede pasar por dos checkouts, y `sessionStorage` no los
 * distingue por si solo. Mandarlo al pedido del checkout anterior seria peor que no ofrecerle nada.
 *
 * <p>No se valida aqui que la URL sea utilizable: de eso se encarga `safeMerchantUrl` al pintar el
 * enlace, que es el unico sitio donde un `javascript:` podria hacer dano, y tenerlo en un solo lugar
 * evita que las dos reglas se separen.
 */
export function rememberMerchantReturnUrl(intentId: string, merchantReturnUrl: string): void {
  if (intentId.length === 0 || merchantReturnUrl.length === 0) {
    return;
  }
  try {
    window.sessionStorage.setItem(
      MERCHANT_RETURN_KEY,
      JSON.stringify({ intentId, merchantReturnUrl }),
    );
  } catch {
    // Sin almacenamiento el checkout funciona igual; lo que se pierde es la salida de emergencia.
  }
}

/**
 * Vuelta recordada de ESTE intent, o null.
 *
 * @param intentId intent que se estaba pagando; un registro de otro intent se ignora
 */
export function recallMerchantReturnUrl(intentId: string | null): string | null {
  if (intentId === null || intentId.length === 0) {
    return null;
  }
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(MERCHANT_RETURN_KEY);
  } catch {
    return null;
  }
  if (raw === null) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // JSON corrupto o manipulado: se sigue sin salida, como si nunca se hubiera recordado.
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return null;
  }
  const record = parsed as { intentId?: unknown; merchantReturnUrl?: unknown };
  const merchantReturnUrl = asNonEmptyString(record.merchantReturnUrl);
  return asNonEmptyString(record.intentId) === intentId ? merchantReturnUrl : null;
}

/** Olvida la vuelta recordada. La llama {@link forgetHostedSession} al cerrar la sesion. */
export function forgetMerchantReturnUrl(): void {
  try {
    window.sessionStorage.removeItem(MERCHANT_RETURN_KEY);
  } catch {
    // Nada que hacer: si no se puede escribir, tampoco se pudo guardar.
  }
}
